import type { DrawerTransport } from "./transport";
import type { DrawerCapability } from "./capability";

/**
 * The drawer-left-open alert (T141, FR-106).
 *
 * THE FEATURE IS ABSENT WHEN UNSUPPORTED, NOT GUESSED. Most ESC/POS printers
 * expose a drawer signal; some do not, and the ones that do not return a byte
 * that decodes to a perfectly plausible-looking "closed". Reporting that as
 * fact would produce an alert that is silently wrong on some hardware — and a
 * drawer alert that cries wolf is worse than none at all, because staff learn
 * to dismiss it and then dismiss the real one.
 *
 * So polling only starts when `capability.drawerStatus` is true, and the
 * observable state is `null` — meaning "unknown", not "closed" — everywhere
 * else. The UI renders nothing for null.
 */

/** How long a drawer may stand open before anyone is told. */
export const OPEN_ALERT_AFTER_MS = 30_000;

/**
 * Polling interval.
 *
 * Local USB I/O, so this costs no quota and no network — but it does cost
 * battery on a tablet, and a drawer that has been open for 30 seconds is not
 * more urgent if noticed 5 seconds sooner.
 */
export const POLL_INTERVAL_MS = 5_000;

export type DrawerState = "open" | "closed" | null;

export interface DrawerStatusSnapshot {
  state: DrawerState;
  /** True once the drawer has been open beyond the threshold. */
  leftOpen: boolean;
  openedAt: number | null;
}

export const UNKNOWN: DrawerStatusSnapshot = { state: null, leftOpen: false, openedAt: null };

type Listener = (snapshot: DrawerStatusSnapshot) => void;

export class DrawerStatusMonitor {
  #timer: ReturnType<typeof setInterval> | null = null;
  #listeners = new Set<Listener>();
  #snapshot: DrawerStatusSnapshot = UNKNOWN;

  constructor(
    private readonly transport: DrawerTransport,
    private readonly now: () => number = Date.now,
  ) {}

  get snapshot(): DrawerStatusSnapshot {
    return this.#snapshot;
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.#snapshot);
    return () => this.#listeners.delete(listener);
  }

  /**
   * Start polling — but only if the hardware genuinely reports drawer state.
   *
   * Returns false when it declines, so the caller can tell "monitoring" from
   * "not monitoring" rather than assuming silence means closed.
   */
  start(capability: DrawerCapability): boolean {
    if (!capability.drawerStatus) {
      this.#emit(UNKNOWN);
      return false;
    }
    if (this.#timer) return true;

    this.#timer = setInterval(() => void this.poll(), POLL_INTERVAL_MS);
    void this.poll();
    return true;
  }

  stop(): void {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
    this.#emit(UNKNOWN);
  }

  async poll(): Promise<void> {
    const state = await this.transport.readDrawerState();

    // A read that fails mid-session means the device went away. Unknown, not
    // closed — the same rule as never having supported it.
    if (state === null) {
      this.#emit(UNKNOWN);
      return;
    }

    if (state === "closed") {
      this.#emit({ state: "closed", leftOpen: false, openedAt: null });
      return;
    }

    const openedAt = this.#snapshot.openedAt ?? this.now();
    this.#emit({
      state: "open",
      openedAt,
      leftOpen: this.now() - openedAt >= OPEN_ALERT_AFTER_MS,
    });
  }

  #emit(snapshot: DrawerStatusSnapshot): void {
    this.#snapshot = snapshot;
    for (const listener of this.#listeners) listener(snapshot);
  }
}
